import {RequestMethod} from '#lit/common/constants/index.js';
import {fetcher} from '#lit/utils/http.js';
import type {
  ExportActivitySummary,
  MatchRatesResponse,
} from '#lit/types/audience_exports.js';
import type {ExportServiceParams} from '#lit/controllers/audience_export_controller/audience_export_controller.js';
import type {AnyObject, Nullable} from '#lit/types/general.js';
import type {DestinationConnection} from '#types/destinations.js';
import {getCSRFToken} from '#lit/utils/auth.js';
import {either} from 'fp-ts';
import {left, right} from '#lit/types/Either.js';
import type {Option} from '#lit/components/base/multiselect_async/index.js';
import {transformMappedFields} from '#lit/components/audiences/audience_export_stepper/utils.js';
import type {SelectedFieldValue} from '#types/journeys.js';
import {
  CampaignType,
  EmptyOptionValue,
  QualtricsListType,
} from '#lit/components/audiences/audience_export_form/constants.js';
import {renderErrorNotification} from '#lit/utils/notifications.js';

export type AudienceExportActivitiesResponse = {
  success: boolean;
  errors: string[];
  export_activities: ExportActivitySummary[];
};

export type ExistingMatchField = {
  label: string;
  value: string;
  dataset_field: string;
};
export type ExistingMatchFieldResponse = {
  is_nested: boolean;
  [key: string]: ExistingMatchField | boolean;
};
export type AudienceExportResponse = {
  message: string;
};

const FORM_CONTENT_TYPE = 'application/json';

const CSV_DOWNLOAD_URL = '/api/v2/export_activities/csv_download';

class AudienceExportsService {
  async loadAudienceExportActivities(
    all = false
  ): Promise<AudienceExportActivitiesResponse> {
    try {
      const params = all ? '?all=true' : '';
      const response = await fetcher<AudienceExportActivitiesResponse>(
        `/api/v2/export_activities${params}`,
        {
          method: RequestMethod.GET,
          responseParser: 'json',
        }
      );
      return {
        success: true,
        errors: [],
        export_activities: response.export_activities,
      };
    } catch (err) {
      const error = err as {errors: string[]};
      return {
        success: false,
        errors: error.errors,
        export_activities: [],
      };
    }
  }

  async getMatchRates(
    audienceExportIDs: number[]
  ): Promise<MatchRatesResponse> {
    try {
      const url = new URL(
        '/api/v2/audience_exports/match_rates',
        location.origin
      );
      audienceExportIDs.forEach(id =>
        url.searchParams.append('audience_export_ids[]', String(id))
      );
      return await fetcher<MatchRatesResponse>(url.toString(), {
        method: RequestMethod.GET,
      });
    } catch (err: unknown) {
      renderErrorNotification(err);
      return {
        matchable: false,
        match_rates: {},
      };
    }
  }

  async updateDestinationMatchFields(params: {
    datasetId: string;
    destinationConnectionId: string;
    audienceId: string;
    mappedMatchFields: Record<string, Nullable<SelectedFieldValue> | string[]>;
    destination_connection: {
      settings: AnyObject;
    };
    platformType: string;
  }) {
    try {
      const body = {
        destination_connection_id: params.destinationConnectionId,
        audience_id: params.audienceId,
        dataset: {...params.mappedMatchFields},
        destination_connection: {
          ...params.destination_connection,
        },
        platform_type: params.platformType,
      };

      const response = await fetcher(
        `/api/v2/datasets/${params.datasetId}/activate`,
        {
          headers: {
            'Content-Type': FORM_CONTENT_TYPE,
            'X-CSRF-Token': getCSRFToken(),
          },
          method: RequestMethod.POST,
          responseParser: 'json',
          body: body,
        }
      );
      return response;
    } catch (err) {
      return either.left(err);
    }
  }

  async getCurrentMatchFieldConfig(
    datasetId?: number,
    destinationId?: number,
    isMlb?: boolean,
    platformType?: string
  ) {
    if (!datasetId || !destinationId) {
      throw Error('missing dataset or destination');
    }
    if (isMlb) {
      const response = await fetcher<ExistingMatchFieldResponse>(
        `/api/v2/datasets/${datasetId}/match_fields/?platform_type=${platformType}`,
        {
          headers: {
            'Content-Type': FORM_CONTENT_TYPE,
            'X-CSRF-Token': getCSRFToken(),
          },
          method: RequestMethod.GET,
        }
      );
      return right(response);
    }
    //TODO (jeff) add proper error handling
    const response = await fetcher<ExistingMatchFieldResponse>(
      `/api/v2/datasets/${datasetId}/match_fields/?destination_connection_id=${destinationId}`,
      {
        headers: {
          'Content-Type': FORM_CONTENT_TYPE,
          'X-CSRF-Token': getCSRFToken(),
        },
        method: RequestMethod.GET,
      }
    );
    return right(response);
  }

  getDestinationSettings(
    destination: DestinationConnection,
    matchType: Nullable<string>
  ) {
    switch (destination.platform_type) {
      case 'Linkedin Ads':
        return {
          linkedin_match_type: matchType,
        };
      case 'DV360':
        return {
          dv360_match_type: matchType,
        };
      case 'Google Adwords':
        return {
          google_ads_match_type: matchType,
        };
      default:
        return {};
    }
  }

  async switchEnabled(
    audienceId: number,
    audienceExportId: number,
    enabled: boolean
  ) {
    const url = `/audiences/${audienceId}/export_enable/${audienceExportId}`;
    const body = {is_enabled: enabled};
    const response = await fetcher(url, {
      headers: {'Content-Type': FORM_CONTENT_TYPE},
      method: RequestMethod.PATCH,
      responseParser: 'json',
      body: body,
    });

    return right(response);
  }

  async createDownload(params: ExportServiceParams) {
    const response = await fetcher(CSV_DOWNLOAD_URL, {
      headers: {'Content-Type': FORM_CONTENT_TYPE},
      method: RequestMethod.POST,
      responseParser: 'json',
      body: params,
    });

    return right(response);
  }

  async createExport(params: ExportServiceParams) {
    //set match fields first
    const platformType = params.destination.platform_type;

    try {
      await this.updateDestinationMatchFields({
        datasetId: params.audienceId.dataset_id.toString(),
        destinationConnectionId: params.destination.id.toString(),
        audienceId: params.audienceId.id.toString(),
        mappedMatchFields: transformMappedFields(
          params.formValues.mappedMatchFields
        ),
        destination_connection: {
          settings: this.getDestinationSettings(
            params.destination,
            params.formValues.matchType
          ),
        },
        platformType,
      });

      const body = this.transformCreateExportServiceParams(params);
      const response = await fetcher('/api/v2/audience_exports', {
        headers: {
          'Content-Type': FORM_CONTENT_TYPE,
          'X-CSRF-Token': getCSRFToken(),
        },
        method: RequestMethod.POST,
        responseParser: platformType === 'csv' ? 'text' : 'json',
        body: body,
      });
      return right(response);
    } catch (err) {
      console.error(err);
      return left(err);
    }
  }

  transformPersonalizationFields(personalizationFields: Option[]) {
    return personalizationFields.map(field => field.value);
  }

  buildViantExportFieldParams(params: ExportServiceParams) {
    return {
      viant_segment_name: params.formValues.viantSegmentName,
      viant_segment_id: params.formValues.viantSegmentId,
      viant_aid: params.formValues.viantAid,
      viant_segment_id_type: params.formValues.viantSegmentIdType,
    };
  }

  buildMarketingCloudExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.campaign_id === EmptyOptionValue ||
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    const businessUnitId =
      params.formValues.new_or_existing_campaign === CampaignType.EXISTING
        ? ''
        : params.formValues.marketingCloudBusinessUnitId;
    const marketingCloudFolder =
      params.formValues.new_or_existing_campaign === CampaignType.EXISTING
        ? ''
        : params.formValues.marketingCloudFolder;
    return {
      existing_list_id: campaign_id,
      marketing_cloud_business_unit_id: businessUnitId,
      marketing_cloud_folder: marketingCloudFolder,
    };
  }

  buildRedditAdsExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildFacebookAdsExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildLobExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      campaign_id: campaign_id,
      campaign_name: params.formValues.campaign_name,
      campaign_type: params.formValues.new_or_existing_campaign,
    };
  }

  buildSendgridExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildTaboolaExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildAmazonAdsExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildAamExportFieldParams(params: ExportServiceParams) {
    return {
      data_source_id: params.formValues.aam_data_source_id,
    };
  }

  buildAdobeExportFieldParams(params: ExportServiceParams) {
    return {
      dataset_id: params.formValues.aep_dataset_id,
    };
  }

  buildAepExportFieldParams(params: ExportServiceParams) {
    return {
      aep_custom_namespace: params.formValues.aep_custom_namespace,
    };
  }

  buildConversicaExportFieldParams(params: ExportServiceParams) {
    return {
      rep_name: params.formValues.repName,
      contact_list_name: params.formValues.contactListName,
    };
  }

  buildPulsePointExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
      pulse_point_advertiser: params.formValues.pulsePointAdvertiser,
      pulse_point_product: params.formValues.pulsePointProduct,
    };
  }

  buildQualtricsExportFieldParams(params: ExportServiceParams) {
    const directory_id =
      params.formValues.qualtricsListType === QualtricsListType.XM_DIRECTORY
        ? params.formValues.qualtricsDirectoryId
        : '';
    const campaign_id =
      params.formValues.qualtricsListType === QualtricsListType.MAILING_LIST
        ? params.formValues.campaign_id
        : '';
    return {
      existing_list_id: campaign_id,
      qualtrics_directory_id: directory_id,
      qualtrics_list_type: params.formValues.qualtricsListType,
    };
  }

  buildDV360ExportFieldParams(params: ExportServiceParams) {
    return {
      dv360_app_id: params.formValues.dv360AppId,
    };
  }

  buildGoogleAdsExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;

    return {
      google_ads_app_id: params.formValues.googleAdsAppId,
      existing_list_id: campaign_id,
    };
  }

  buildCriteoExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildCriteoRetailMediaExportFieldParams(params: ExportServiceParams) {
    const campaign_id =
      params.formValues.new_or_existing_campaign === CampaignType.NEW
        ? ''
        : params.formValues.campaign_id;
    return {
      existing_list_id: campaign_id,
    };
  }

  buildLiveRampActivationExportFieldParams(params: ExportServiceParams) {
    return {
      distribution_manager_ids: params.formValues.distribution_manager_ids,
    };
  }

  buildExportFieldParams(params: ExportServiceParams) {
    switch (params.formValues.platform_type) {
      case 'Lob':
        return this.buildLobExportFieldParams(params);
      case 'Viant':
        return this.buildViantExportFieldParams(params);
      case 'Marketing Cloud':
        return this.buildMarketingCloudExportFieldParams(params);
      case 'Conversica':
        return this.buildConversicaExportFieldParams(params);
      case 'Adobe Audience Manager':
        return this.buildAamExportFieldParams(params);
      case 'Adobe':
        return this.buildAdobeExportFieldParams(params);
      case 'Adobe Experience Platform':
        return this.buildAepExportFieldParams(params);
      case 'SendGrid':
        return this.buildSendgridExportFieldParams(params);
      case 'Taboola':
        return this.buildTaboolaExportFieldParams(params);
      case 'Amazon Ads':
        return this.buildAmazonAdsExportFieldParams(params);
      case 'PulsePoint':
        return this.buildPulsePointExportFieldParams(params);
      case 'Criteo Marketing Solutions':
        return this.buildCriteoExportFieldParams(params);
      case 'Criteo Retail Media':
        return this.buildCriteoRetailMediaExportFieldParams(params);
      case 'DV360':
        return this.buildDV360ExportFieldParams(params);
      case 'Google Adwords':
        return this.buildGoogleAdsExportFieldParams(params);
      case 'Qualtrics':
        return this.buildQualtricsExportFieldParams(params);
      case 'Reddit Ads':
        return this.buildRedditAdsExportFieldParams(params);
      case 'Facebook':
        return this.buildFacebookAdsExportFieldParams(params);
      default:
        return {};
    }
  }

  buildAdditionalFieldParams(params: ExportServiceParams) {
    /**
     * FIXME(joshua): Only cast to DTO when building here.
     */
    return params.formValues.additionalFields;
  }

  buildExtraConfigFieldParams(params: ExportServiceParams) {
    if (params.formValues.platform_type === 'LiveRamp Activation') {
      return this.buildLiveRampActivationExportFieldParams(params);
    }
    return null;
  }

  transformCreateExportServiceParams(params: ExportServiceParams) {
    const body: AnyObject = {
      audience_id: params.audienceId.id,
      dataset_group_id: params.audienceId.dataset_group_id,
      audience_export: {
        audience_name: params.formValues.exportName,
        platform_type: params.destination.platform_type,
        ends_at: params.formValues.endDate,
        enhanced_match_rate_enabled: params.formValues.enhancedMatchRateEnabled,
        //FIX ME(Jeff)
        export_type:
          params.formValues.platform_type === 'Attentive' ||
          params.destination.is_mlb
            ? 'snapshots'
            : params.formValues.export_inclusion_criteria,
        sync_frequency: params.formValues.syncFrequency,
        custom_fields: this.transformPersonalizationFields(
          params.formValues.personalizationFields
        ),
        all_personalization_fields_selected:
          params.formValues.all_personalization_fields_selected,
        all_gl_fields_selected:
          params.formValues.ALL_GL_GENERATED_FIELDS_SELECTED,
        additional_fields: this.buildAdditionalFieldParams(params),
        audience_segment_id: params.formValues.audience_segment_id,
        export_fields: this.buildExportFieldParams(params),
        extra_config: this.buildExtraConfigFieldParams(params),
        field_mappings: {
          email_list_id: params.formValues.email_list_id,
          campaign_type: params.formValues.campaign_type,
          campaign_id: params.formValues.audience_export_campaign_id,
          variation: params.formValues.variation,
          api_type: params.formValues.api_type,
        },
        privacy_settings: {
          losp: params.formValues.losp,
          opt_in_language_approved_vetted:
            params.formValues.opt_in_language_approved_vetted,
          end_user_opt_in: params.formValues.end_user_opt_in,
          third_party_name: params.formValues.third_party_name,
          club_sponsor_advertiser_io:
            params.formValues.club_sponsor_advertiser_io,
          club_sponsor_advertiser_data_share:
            params.formValues.club_sponsor_advertiser_data_share,
          info_collected_via_mlb_privacy_policy:
            params.formValues.info_collected_via_mlb_privacy_policy,
        },
      },
    };

    if (params.formValues.schedule) {
      body.audience_export.schedule = params.formValues.schedule;
    }

    body.audience_export.user_consent = params.formValues.userConsent;

    switch (params.destination.platform_type) {
      case 'Linkedin Ads': {
        body.audience_export.linkedin_match_type = params.formValues.matchType;
        break;
      }
      case 'Google Adwords': {
        body.audience_export.google_ads_match_type =
          params.formValues.matchType;
        body.audience_export.user_data_consent = params.formValues.dataConsent;
        body.audience_export.ad_personalization_consent =
          params.formValues.personalizationConsent;
        break;
      }
      case 'DV360': {
        body.audience_export.dv360_match_type = params.formValues.matchType;
        body.audience_export.user_data_consent = params.formValues.dataConsent;
        body.audience_export.ad_personalization_consent =
          params.formValues.personalizationConsent;
        break;
      }
      case 'BigQuery':
      case 'Snowflake':
      case 'MessageGears Snowflake': {
        body.audience_export.table_name = params.formValues.tableName;
        break;
      }
      case 'Marketo List': {
        body.audience_export.marketo_audience_type =
          params.formValues.marketo_audience_type;
        body.audience_export.marketo_custom_object =
          params.formValues.marketo_custom_object;
        break;
      }
    }
    if (!params.destination.is_mlb) {
      body.audience_export.destination_connection_id = params.destination.id;
    }
    return body;
  }
}

export const audienceExportsService = new AudienceExportsService();
